import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Layout from '../components/Layout'
import { API, api, getJwt, fileSize } from '../lib/api'
import { useUploads } from '../lib/uploads'

interface ReviewStats { comments: number; unresolved: number; approvals: number; last_comment_at: string | null }
interface Share { token: string; path: string; root?: string; type: 'dir' | 'file'; url: string; downloads: number; created_at: string; expires_at: string | null; allow_download: boolean; review: boolean; stats?: ReviewStats }
interface Entry { name: string; path: string; type: 'dir' | 'file'; size: number; modified: string; mime: string | null; share: Share | null; review_share: Share | null }
type Kind = 'dir' | 'image' | 'video' | 'audio' | 'pdf' | 'other'
type View = 'list' | 'grid'

const btn = 'px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors'
const kindOf = (e: Entry): Kind => e.type === 'dir' ? 'dir' : /^video\//.test(e.mime || '') ? 'video' : /^image\//.test(e.mime || '') && !/\.(svg|psd)$/i.test(e.name) ? 'image'
  : /^audio\//.test(e.mime || '') ? 'audio' : e.mime === 'application/pdf' ? 'pdf' : 'other'
const ICON: Record<Kind, string> = { dir: '📁', video: '🎬', image: '🖼️', audio: '🎵', pdf: '📄', other: '📎' }
const icon = (e: Entry) => ICON[kindOf(e)]
const previewable = (e: Entry) => kindOf(e) !== 'dir' && kindOf(e) !== 'other'
const fmtDate = (d: string) => new Date(d).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })
const PUBLIC = window.location.origin

/** Vignette avec repli sur l'icône si le serveur ne peut pas la générer. */
function Thumb({ e, src, className = '' }: { e: Entry; src: string; className?: string }) {
  const [err, setErr] = useState(false)
  const k = kindOf(e)
  if ((k !== 'image' && k !== 'video') || err) return <div className={`flex items-center justify-center text-4xl ${className}`}>{icon(e)}</div>
  return <img src={src} alt="" loading="lazy" onError={() => setErr(true)} className={`object-cover w-full h-full ${className}`} />
}

/** Visionneuse plein écran : photo, vidéo, audio, PDF — flèches ← → pour naviguer. */
function Preview({ items, index, url, onClose, onNav }: { items: Entry[]; index: number; url: (e: Entry, inline?: boolean) => string; onClose: () => void; onNav: (i: number) => void }) {
  const e = items[index]; const k = kindOf(e); const src = url(e, true)
  useEffect(() => {
    const h = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose(); if (ev.key === 'ArrowLeft' && index > 0) onNav(index - 1); if (ev.key === 'ArrowRight' && index < items.length - 1) onNav(index + 1) }
    window.addEventListener('keydown', h); document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = '' }
  }, [index, items.length, onClose, onNav])
  const navBtn = 'absolute top-1/2 -translate-y-1/2 text-4xl md:text-5xl text-blanc/80 hover:text-orange bg-black/50 md:bg-transparent rounded-full w-11 h-11 flex items-center justify-center leading-none'
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={onClose}>
      <div className="flex items-center gap-4 px-4 py-3 text-sm" onClick={ev => ev.stopPropagation()}>
        <span className="flex-1 truncate">{icon(e)} {e.name}</span>
        <span className="text-acier text-xs hidden sm:inline">{fileSize(e.size)} · {index + 1}/{items.length}</span>
        <a href={url(e)} className="text-orange text-xs font-bold uppercase tracking-widest hover:underline">⬇ Télécharger</a>
        <button onClick={onClose} aria-label="Fermer" className="text-acier hover:text-orange text-3xl leading-none">×</button>
      </div>
      <div className="flex-1 min-h-0 relative flex items-center justify-center px-2 md:px-16 pb-3">
        {index > 0 && <button onClick={ev => { ev.stopPropagation(); onNav(index - 1) }} aria-label="Précédent" className={`${navBtn} left-2`}>‹</button>}
        <div className="w-full h-full flex items-center justify-center" onClick={ev => { if (ev.target === ev.currentTarget) onClose() }}>
          {k === 'image' && <img src={src} alt={e.name} className="max-w-full max-h-full object-contain" />}
          {k === 'video' && <video key={src} src={src} controls autoPlay playsInline className="max-w-full max-h-full" />}
          {k === 'audio' && <audio key={src} src={src} controls autoPlay className="w-full max-w-xl" />}
          {k === 'pdf' && <iframe src={src} title={e.name} className="w-full h-full bg-white border-0" />}
        </div>
        {index < items.length - 1 && <button onClick={ev => { ev.stopPropagation(); onNav(index + 1) }} aria-label="Suivant" className={`${navBtn} right-2`}>›</button>}
      </div>
    </div>
  )
}

export default function Fichiers() {
  // Dossier courant dans l'URL (?p=…) : survit au rafraîchissement, bouton Précédent fonctionnel
  const [params, setParams] = useSearchParams()
  const path = params.get('p') || ''
  const setPath = (p: string) => setParams(p ? { p } : {})
  const [entries, setEntries] = useState<Entry[]>([])
  const [shares, setShares] = useState<Share[]>([])
  const [msg, setMsg] = useState('')
  const [pct, setPct] = useState<number | null>(null)
  const [jwt, setJwt] = useState('')
  const [drag, setDrag] = useState(false)
  const [showShares, setShowShares] = useState(false)
  const [view, setView] = useState<View>(() => { try { return (localStorage.getItem('pivot_files_view') as View) || 'grid' } catch { return 'grid' } })
  const [preview, setPreview] = useState<number | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const { start } = useUploads()

  const [loadError, setLoadError] = useState('')
  const load = async (p = path) => {
    try {
      const [l, s] = await Promise.all([api<{ entries: Entry[] }>(`/stock?path=${encodeURIComponent(p)}`), api<{ shares: Share[] }>('/stock/shares')])
      setEntries(l.entries); setShares(s.shares); setLoadError('')
    } catch (e: any) { setLoadError(e.message) } // on garde la liste précédente à l'écran
  }
  useEffect(() => { getJwt().then(setJwt); load(path) }, [path])
  useEffect(() => { try { localStorage.setItem('pivot_files_view', view) } catch {} }, [view])

  const crumbs = path ? path.split('/') : []
  const go = (p: string) => { setPath(p); setMsg(''); setPreview(null) }
  const previews = entries.filter(previewable)

  async function doUpload(files: FileList | File[]) {
    const list = Array.from(files); if (!list.length) return
    setPct(0); setMsg(`Téléversement de ${list.length} fichier${list.length > 1 ? 's' : ''}…`)
    try { await start(`/stock/upload?path=${encodeURIComponent(path)}`, list); setMsg('✅ Téléversement terminé.'); await load() }
    catch (e: any) { setMsg('Erreur : ' + e.message) } finally { setPct(null) }
  }
  async function mkdir() {
    const name = prompt('Nom du nouveau dossier :'); if (!name?.trim()) return
    try { await api('/stock/mkdir', { method: 'POST', json: { path, name } }); await load() } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }
  async function rename(e: Entry) {
    const name = prompt('Nouveau nom :', e.name); if (!name?.trim() || name === e.name) return
    try { await api('/stock/rename', { method: 'POST', json: { path: e.path, name } }); await load() } catch (err: any) { setMsg('Erreur : ' + err.message) }
  }
  async function remove(e: Entry) {
    if (!confirm(`Supprimer ${e.type === 'dir' ? 'le dossier et tout son contenu' : 'le fichier'} « ${e.name} » ?`)) return
    try { await api(`/stock?path=${encodeURIComponent(e.path)}`, { method: 'DELETE' }); await load(); setMsg('Supprimé.') } catch (err: any) { setMsg('Erreur : ' + err.message) }
  }
  async function copy(text: string) {
    try { await navigator.clipboard.writeText(text); setMsg('🔗 Lien copié : ' + text) } catch { prompt('Copiez le lien :', text) }
  }
  async function share(e: Entry, review = false) {
    try {
      const r = await api<{ share: Share }>('/stock/share', { method: 'POST', json: { path: e.path, review } })
      await copy(r.share.url); await load()
      if (review) setMsg(m => m + ' · lien de revue : vos clients voient les vidéos et photos, commentent et approuvent, sans pouvoir télécharger.')
      else if (!r.share.allow_download) setMsg(m => m + ' · aperçu seulement (activez « Téléch. » pour permettre le téléchargement).')
    } catch (err: any) { setMsg('Erreur : ' + err.message) }
  }
  const reviewLabel = (s: Share) => { const st = s.stats; if (!st) return ''; const parts = [st.comments ? `${st.comments} comm.${st.unresolved ? ` (${st.unresolved} à traiter)` : ''}` : '', st.approvals ? `${st.approvals} approuvé${st.approvals > 1 ? 's' : ''}` : '']; return parts.filter(Boolean).join(' · ') }
  async function toggleDownload(s: Share) {
    try {
      const r = await api<{ share: Share }>('/stock/share/update', { method: 'POST', json: { token: s.token, allow_download: !s.allow_download } })
      await load(); setMsg(r.share.allow_download ? '⬇ Téléchargement permis sur ce lien.' : '👁 Aperçu seulement : téléchargement désactivé sur ce lien.')
    } catch (err: any) { setMsg('Erreur : ' + err.message) }
  }
  const DlToggle = ({ s }: { s: Share }) => (
    <button onClick={() => toggleDownload(s)} title={s.allow_download ? 'Le public peut télécharger. Cliquez pour passer en aperçu seulement.' : 'Aperçu seulement. Cliquez pour permettre le téléchargement.'}
      className={`text-xs uppercase tracking-widest ${s.allow_download ? 'text-green-300' : 'text-acier'} hover:text-orange`}>
      {s.allow_download ? '⬇ Téléch. permis' : '👁 Aperçu seul.'}
    </button>
  )
  async function revoke(token: string) {
    try { await api(`/stock/share?token=${token}`, { method: 'DELETE' }); await load(); setMsg('Lien révoqué.') } catch (err: any) { setMsg('Erreur : ' + err.message) }
  }
  const dl = (e: Entry, inline = false) => `${API}/stock/download?path=${encodeURIComponent(e.path)}&access_token=${jwt}${inline ? '&inline=1' : ''}`
  const zipUrl = (p: string) => `${API}/stock/download?path=${encodeURIComponent(p)}&access_token=${jwt}`
  const thumb = (e: Entry) => `${API}/stock/thumb?path=${encodeURIComponent(e.path)}&access_token=${jwt}`
  const shareUrl = (s: Share) => s.url.startsWith('http') ? s.url : `${PUBLIC}${s.url}`
  const open = (e: Entry) => { if (e.type === 'dir') return go(e.path); const i = previews.indexOf(e); if (i >= 0) setPreview(i); else window.open(dl(e), '_blank') }
  const folderName = crumbs.length ? crumbs[crumbs.length - 1] : 'stock'

  const actions = (e: Entry) => (
    <>
      {e.type === 'file'
        ? <a href={dl(e)} className="text-acier hover:text-orange">Télécharger</a>
        : <a href={zipUrl(e.path)} className="text-acier hover:text-orange" title="Télécharger le dossier complet en .zip">⬇ .zip</a>}
      {e.share
        ? <><button onClick={() => copy(shareUrl(e.share!))} className="text-green-300 hover:underline">Copier lien</button><DlToggle s={e.share} /></>
        : <button onClick={() => share(e)} className="text-orange hover:underline">Partager</button>}
      {previewable(e) || e.type === 'dir'
        ? e.review_share
          ? <a href={shareUrl(e.review_share)} target="_blank" rel="noreferrer" title={`Lien de revue · ${reviewLabel(e.review_share) || 'aucun commentaire'}`} className="text-sky-300 hover:underline">💬 Revue{e.review_share.stats?.comments ? ` (${e.review_share.stats.comments})` : ''}</a>
          : <button onClick={() => share(e, true)} title="Créer un lien de revue : aperçu sans téléchargement, commentaires et approbation" className="text-acier hover:text-sky-300">Revue</button>
        : null}
      <button onClick={() => rename(e)} className="text-acier hover:text-orange">Renommer</button>
      <button onClick={() => remove(e)} className="text-acier hover:text-red-300">Suppr.</button>
    </>
  )

  return (
    <Layout title="Fichiers">
      {preview !== null && previews[preview] && <Preview items={previews} index={preview} url={dl} onClose={() => setPreview(null)} onNav={setPreview} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <a href="/admin" className="text-xs uppercase tracking-widest text-acier hover:text-orange">← Admin</a>
        <div className="flex flex-wrap gap-2">
          <div className="flex border border-white/10 rounded-sm overflow-hidden">
            <button onClick={() => setView('grid')} title="Vignettes" className={`${btn} rounded-none ${view === 'grid' ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'}`}>▦</button>
            <button onClick={() => setView('list')} title="Liste" className={`${btn} rounded-none ${view === 'list' ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'}`}>☰</button>
          </div>
          <button onClick={() => setShowShares(v => !v)} className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10`}>🔗 Liens partagés ({shares.length})</button>
          {entries.length > 0 && <a href={zipUrl(path)} className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10`} title={`Télécharger « ${folderName} » au complet en .zip`}>⬇ Dossier .zip</a>}
          <button onClick={mkdir} className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10`}>+ Dossier</button>
          <button onClick={() => fileInput.current?.click()} disabled={pct !== null} className={`${btn} bg-orange hover:bg-orange-hover disabled:opacity-50`}>{pct !== null ? 'Envoi en cours…' : '⬆ Téléverser'}</button>
          <input ref={fileInput} type="file" multiple className="hidden" onChange={e => { if (e.target.files) doUpload(e.target.files); e.target.value = '' }} />
        </div>
      </div>

      {/* Fil d'Ariane */}
      <div className="mt-6 flex flex-wrap items-center gap-1 text-sm">
        <button onClick={() => go('')} className={crumbs.length ? 'text-acier hover:text-orange' : 'text-blanc'}>Stock</button>
        {crumbs.map((c, i) => {
          const p = crumbs.slice(0, i + 1).join('/')
          return <span key={p} className="flex items-center gap-1"><span className="text-acier">/</span>
            <button onClick={() => go(p)} className={i === crumbs.length - 1 ? 'text-blanc' : 'text-acier hover:text-orange'}>{c}</button></span>
        })}
      </div>
      {msg && <p className="text-xs text-acier mt-3 break-all">{msg}</p>}
      {loadError && <div className="mt-4 border border-red-400/40 bg-red-400/10 rounded px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3"><span>Impossible de charger le dossier : {loadError}</span><button onClick={() => load()} className={`${btn} bg-white/10 hover:bg-white/20 border border-white/10`}>↻ Réessayer</button></div>}
      {pct !== null && <p className="text-xs text-acier mt-2">Progression dans le panneau en bas à droite. Vous pouvez continuer à naviguer.</p>}

      {showShares && (
        <div className="mt-6 border border-white/10 rounded p-4 bg-white/[0.02]">
          <h2 className="font-display text-xl tracking-wide mb-3">Liens de partage actifs</h2>
          {shares.length === 0 ? <p className="text-acier text-sm">Aucun lien. Cliquez « Partager » sur un fichier ou un dossier.</p> : (
            <div className="space-y-2 text-sm">
              {shares.map(s => (
                <div key={s.token} className="flex flex-wrap items-center gap-3 border-t border-white/5 pt-2">
                  <span className="flex-1 min-w-[160px] truncate">{s.type === 'dir' ? '📁' : '📎'} {s.root === 'clients' ? <span className="text-acier">livrables client / </span> : ''}{s.path}{s.review && <span className="ml-2 text-[10px] uppercase tracking-wider text-sky-300">💬 revue</span>}</span>
                  {s.review
                    ? <span className={`text-xs ${s.stats?.unresolved ? 'text-orange' : 'text-acier'}`}>{reviewLabel(s) || 'aucun commentaire'}</span>
                    : <><span className="text-acier text-xs">{s.downloads} téléch.</span><DlToggle s={s} /></>}
                  <a href={shareUrl(s)} target="_blank" rel="noreferrer" className="text-acier hover:text-orange text-xs uppercase tracking-widest">Ouvrir</a>
                  <button onClick={() => copy(shareUrl(s))} className="text-orange text-xs uppercase tracking-widest hover:underline">Copier</button>
                  <button onClick={() => revoke(s.token)} className="text-acier hover:text-red-300 text-xs uppercase tracking-widest">Révoquer</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Zone de dépôt + contenu */}
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files) }}
        className={`mt-6 border rounded transition-colors ${drag ? 'border-orange bg-orange/5' : 'border-white/10'} ${view === 'grid' ? 'p-4' : 'overflow-hidden'}`}>

        {entries.length === 0 && !loadError && (
          <div className="px-4 py-10 text-center text-acier text-sm">Dossier vide : glissez-déposez des fichiers ici ou cliquez « Téléverser ».</div>
        )}

        {view === 'grid' && entries.length > 0 && (
          <ul className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {entries.map(e => (
              <li key={e.path} className="group border border-white/10 rounded overflow-hidden bg-white/[0.02] flex flex-col">
                <button onClick={() => open(e)} className="relative block w-full aspect-[16/10] bg-[#161616] overflow-hidden text-left" title={e.type === 'dir' ? 'Ouvrir le dossier' : previewable(e) ? 'Aperçu' : 'Ouvrir'}>
                  {jwt && <Thumb e={e} src={thumb(e)} className="h-full w-full transition-transform duration-300 group-hover:scale-105" />}
                  {kindOf(e) === 'video' && <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 border-2 border-white flex items-center justify-center text-sm pl-0.5">▶</span>}
                  <span className="absolute top-2 right-2 flex gap-1">
                    {e.share && <span className="text-[10px] uppercase tracking-wider bg-black/70 text-green-300 px-1.5 py-0.5 rounded">🔗 partagé</span>}
                    {e.review_share && <span className={`text-[10px] uppercase tracking-wider bg-black/70 px-1.5 py-0.5 rounded ${e.review_share.stats?.unresolved ? 'text-orange' : 'text-sky-300'}`}>💬 revue{e.review_share.stats?.unresolved ? ` ${e.review_share.stats.unresolved}` : ''}</span>}
                  </span>
                </button>
                <div className="p-3 flex-1 flex flex-col gap-2">
                  <button onClick={() => open(e)} className="text-left text-sm truncate hover:text-orange" title={e.name}>{e.name}</button>
                  <div className="text-[11px] text-acier">{e.type === 'dir' ? 'Dossier' : fileSize(e.size)} · {fmtDate(e.modified)}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest mt-auto">{actions(e)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {view === 'list' && entries.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-acier text-[11px] uppercase tracking-widest">
              <tr><th className="text-left px-4 py-3">Nom</th><th className="text-right px-4 py-3 hidden sm:table-cell">Taille</th><th className="text-left px-4 py-3 hidden md:table-cell">Modifié</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.path} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-2">
                    <button onClick={() => open(e)} className="flex items-center gap-3 hover:text-orange text-left">
                      <span className="w-14 h-9 rounded-sm bg-[#161616] overflow-hidden flex items-center justify-center shrink-0">
                        {jwt && <Thumb e={e} src={thumb(e)} className="text-lg" />}
                      </span>
                      <span className="truncate">{e.name}</span>
                    </button>
                    {e.share && <span className="ml-2 text-[10px] uppercase tracking-wider text-green-300">🔗 partagé</span>}
                    {e.review_share && <span className={`ml-2 text-[10px] uppercase tracking-wider ${e.review_share.stats?.unresolved ? 'text-orange' : 'text-sky-300'}`}>💬 revue{e.review_share.stats?.unresolved ? ` · ${e.review_share.stats.unresolved} à traiter` : ''}</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-acier hidden sm:table-cell">{e.type === 'dir' ? '—' : fileSize(e.size)}</td>
                  <td className="px-4 py-2 text-acier hidden md:table-cell">{fmtDate(e.modified)}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-3 text-xs uppercase tracking-widest whitespace-nowrap">{actions(e)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-acier mt-3">Les fichiers sont stockés sur le serveur dans <code>stock/</code>. Un lien de partage est public : quiconque possède le lien ouvre une page d'aperçu (lecteur vidéo, photo, audio, PDF). Le téléchargement n'est permis que si « Téléch. permis » est activé sur le lien (par défaut : aperçu seul pour un fichier, téléchargement permis pour un dossier). Révoquez-le pour couper l'accès.</p>
      <p className="text-xs text-acier mt-2"><span className="text-sky-300">💬 Revue</span> : un second type de lien, sans téléchargement, où le client fait défiler toutes les vidéos et photos du dossier, laisse des commentaires liés à un moment précis de la vidéo ou à un point de la photo, et approuve chaque fichier. Vous recevez un courriel récapitulatif à chaque nouvelle activité. Ouvrez le lien en étant connecté ici pour répondre en tant que Pivot Média et marquer les commentaires réglés.</p>
    </Layout>
  )
}
